export type AdminEnvironment = "prod" | "dev";

export type AdminEnvironmentConfig = {
  key: AdminEnvironment;
  label: string;
  apiBaseUrl: string;
};

const ENVIRONMENT_STORAGE_KEY = "quazar_admin_environment";

const normalizeBaseUrl = (value: string | undefined, fallback: string) =>
  (value?.trim() || fallback).replace(/\/$/, "");

const prodApiBaseUrl = normalizeBaseUrl(
  import.meta.env.VITE_PROD_API_BASE_URL,
  import.meta.env.VITE_API_BASE_URL || "https://api.quazar.co.kr/api",
);

const devApiBaseUrl = normalizeBaseUrl(
  import.meta.env.VITE_DEV_API_BASE_URL,
  import.meta.env.DEV ? "http://localhost:8000/api" : "",
);

export const ADMIN_ENVIRONMENTS: Record<AdminEnvironment, AdminEnvironmentConfig> = {
  prod: { key: "prod", label: "운영", apiBaseUrl: prodApiBaseUrl },
  dev: { key: "dev", label: "개발", apiBaseUrl: devApiBaseUrl },
};

export const getAdminEnvironment = (): AdminEnvironment => {
  const stored = localStorage.getItem(ENVIRONMENT_STORAGE_KEY);
  return stored === "dev" && devApiBaseUrl ? "dev" : "prod";
};

export const setAdminEnvironment = (environment: AdminEnvironment) => {
  localStorage.setItem(ENVIRONMENT_STORAGE_KEY, environment);
};

export const getAdminEnvironmentConfig = (environment = getAdminEnvironment()) =>
  ADMIN_ENVIRONMENTS[environment];

export const buildEnvironmentApiUrl = (environment: AdminEnvironment, path: string) => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${ADMIN_ENVIRONMENTS[environment].apiBaseUrl}${normalizedPath}`;
};

export const buildSelectedAdminApiUrl = (path: string) =>
  buildEnvironmentApiUrl(getAdminEnvironment(), path);

export const getEnvironmentTokenKey = (
  environment: AdminEnvironment,
  tokenType: "access" | "refresh",
) => `quazar_admin_${environment}_${tokenType}_token`;

export const getAdminEnvironmentAvailabilityKey = (environment: AdminEnvironment) =>
  `quazar_admin_${environment}_available`;

export const setAdminEnvironmentAvailability = (
  environment: AdminEnvironment,
  available: boolean,
) => {
  localStorage.setItem(getAdminEnvironmentAvailabilityKey(environment), String(available));
};

export const isAdminEnvironmentAvailable = (environment: AdminEnvironment) =>
  Boolean(ADMIN_ENVIRONMENTS[environment].apiBaseUrl)
  && localStorage.getItem(getAdminEnvironmentAvailabilityKey(environment)) !== "false";
